import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { Loader2, ArrowLeft, Play, Trophy } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Campaign = () => {
  const navigate = useNavigate();
  const [decks, setDecks] = useState([]);
  const [selectedDeckId, setSelectedDeckId] = useState('');
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    fetchDecks();
  }, []);

  const fetchDecks = async () => {
    try {
      const response = await axios.get(`${API}/decks`);
      setDecks(response.data);
      if (response.data.length > 0) {
        setSelectedDeckId(response.data[0].id);
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching decks:', error);
      toast({
        title: 'Error',
        description: 'Failed to load decks',
        variant: 'destructive'
      });
      setLoading(false);
    }
  };

  const startGame = async () => {
    if (!selectedDeckId) {
      toast({
        title: 'Error',
        description: 'Please select a deck first',
        variant: 'destructive'
      });
      return;
    }

    setStarting(true);
    try {
      const response = await axios.post(`${API}/game/start`, {
        deck_id: selectedDeckId
      });
      
      toast({
        title: 'Game Started!',
        description: 'Good luck!'
      });
      
      navigate(`/game/${response.data.id}`);
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to start game',
        variant: 'destructive'
      });
      setStarting(false);
    }
  };

  if (loading) {
    return (
      <div className='flex items-center justify-center h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900'>
        <Loader2 className='h-12 w-12 animate-spin text-white' />
      </div>
    );
  }

  return (
    <div className='min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 p-6'>
      <div className='container mx-auto'>
        <div className='mb-6 flex items-center justify-between'>
          <div className='flex items-center gap-4'>
            <Link to='/'>
              <Button variant='outline' size='sm' data-testid='back-home-btn'>
                <ArrowLeft className='h-4 w-4 mr-2' /> Home
              </Button>
            </Link>
            <h1 className='text-4xl font-bold text-white' data-testid='campaign-title'>Campaign Mode</h1>
          </div>
        </div>

        {decks.length === 0 ? (
          <Card className='bg-white/10 backdrop-blur-sm border-white/20 max-w-2xl mx-auto'>
            <CardHeader>
              <CardTitle className='text-white'>No Decks Available</CardTitle>
              <CardDescription className='text-gray-300'>
                You need to create a deck before playing
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Link to='/deck-builder'>
                <Button className='w-full'>Go to Deck Builder</Button>
              </Link>
            </CardContent>
          </Card>
        ) : (
          <div className='grid md:grid-cols-2 gap-6 max-w-6xl mx-auto'>
            <Card className='bg-white/10 backdrop-blur-sm border-white/20'>
              <CardHeader>
                <CardTitle className='text-white'>Select Your Deck</CardTitle>
                <CardDescription className='text-gray-300'>
                  Choose which deck to battle with
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Select value={selectedDeckId} onValueChange={setSelectedDeckId}>
                  <SelectTrigger className='bg-white/20 border-white/30 text-white' data-testid='deck-selector'>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {decks.map(deck => (
                      <SelectItem key={deck.id} value={deck.id}>{deck.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Button 
                  onClick={startGame} 
                  disabled={starting}
                  className='w-full mt-4'
                  data-testid='start-game-btn'
                >
                  {starting ? (
                    <><Loader2 className='h-4 w-4 animate-spin mr-2' /> Starting...</>
                  ) : (
                    <><Play className='h-4 w-4 mr-2' /> Start Battle</>
                  )}
                </Button>
              </CardContent>
            </Card>

            <Card className='bg-white/10 backdrop-blur-sm border-white/20'>
              <CardHeader>
                <CardTitle className='text-white flex items-center gap-2'>
                  <Trophy className='h-6 w-6 text-yellow-400' />
                  Campaign Progress
                </CardTitle>
                <CardDescription className='text-gray-300'>
                  Battle through 30 levels
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className='space-y-2'>
                  <div className='bg-white/10 rounded p-3 border-2 border-green-500'>
                    <div className='flex justify-between items-center'>
                      <span className='text-white font-bold'>Level 1</span>
                      <Badge className='bg-green-600'>Available</Badge>
                    </div>
                    <p className='text-gray-400 text-sm mt-1'>AI Opponent - Easy</p>
                  </div>
                  
                  {[2, 3, 4, 5].map(level => (
                    <div key={level} className='bg-white/10 rounded p-3 opacity-50'>
                      <div className='flex justify-between items-center'>
                        <span className='text-white font-bold'>Level {level}</span>
                        <Badge variant='secondary'>Locked</Badge>
                      </div>
                      <p className='text-gray-400 text-sm mt-1'>Complete previous level to unlock</p>
                    </div>
                  ))}
                </div>
                
                <p className='text-gray-400 text-sm mt-4 text-center'>
                  30 levels of increasing difficulty coming soon!
                </p>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default Campaign;